import { Score } from 'modules/core/supabase/tables/match-chronology';
import { Fouls } from 'modules/stream/domain/interfaces/game-info/state';
import { parseEvent, setFoulsOnPeriod, setGoals } from 'modules/stream/store/game-info/game-info';
import { setActiveBoard } from 'modules/template-editor/store/templates/templates';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AppDispatch } from 'store/store';
import supabase from '../../../../core/supabase/supabase';
import { Event } from '../../../domain/interfaces/events';
import { getFoulsByPeriod } from '../../../domain/utils/get-fouls';
import { addEvents } from '../../../store/overlay/overlay';
import { selectIsBoardLocked } from '../../../store/overlay/selectors';
import { selectPeriod } from '../../../store/time/selectors';
import { setPeriod, setTimer } from '../../../store/time/time';
import useGameInfo from '../use-game-info';

function useInitSubscribeOverlay() {
  const dispatch = useDispatch<AppDispatch>();
  const { matchId: id } = useParams();

  const matchId = useMemo(() => (id ? parseInt(id) : 0), [id]);
  const { organizer, championship } = useGameInfo();
  const isBoardLocked = useSelector(selectIsBoardLocked);
  const statePeriod = useSelector(selectPeriod);

  useEffect(() => {
    supabase.matchChronology.observeChanges(matchId);

    return () => {
      supabase.matchChronology.stopObserveChanges();
    };
  }, [dispatch, matchId]);

  useEffect(() => {
    const scoreCallback = (score: Score | null) => {
      if (!score) {
        supabase.overlayLogs.createWarning({
          message: 'No score received',
        });
        return;
      }
      dispatch(setGoals({ first: score[0].score, second: score[1].score }));
    };

    supabase.matchChronology.score.subscribe(scoreCallback);

    return () => {
      supabase.matchChronology.score.unsubscribe(scoreCallback);
    };
  }, [organizer?.id, championship?.id, matchId, championship?.overlayId, dispatch]);

  useEffect(() => {
    const activeBoardCallback = (id: number | null) => {
      if (isBoardLocked) return;
      dispatch(setActiveBoard({ id: id ?? undefined }));
    };

    const eventsCallback = (newEvents: Event[]) => {
      dispatch(parseEvent({ events: newEvents }));
      if (isBoardLocked) return;
      dispatch(addEvents(newEvents));
    };

    supabase.matchChronology.activeBoardId.subscribe(activeBoardCallback);
    supabase.matchChronology.events.subscribe(eventsCallback);

    return () => {
      supabase.matchChronology.activeBoardId.unsubscribe(activeBoardCallback);
      supabase.matchChronology.events.unsubscribe(eventsCallback);
    };
  }, [dispatch, isBoardLocked]);

  useEffect(() => {
    const timeCallback = ({ seconds, period }: { seconds: number; period: number }) => {
      dispatch(setTimer({ milliseconds: seconds * 1000 }));
      dispatch(setPeriod(period));
      if (statePeriod !== period) {
        getFoulsByPeriod(matchId, period).then((fouls: Fouls) => {
          dispatch(setFoulsOnPeriod({ ...fouls, period }));
        });
      }
    };

    supabase.matchChronology.currentTime.subscribe(timeCallback);

    return () => {
      supabase.matchChronology.currentTime.unsubscribe(timeCallback);
    };
  }, [dispatch, matchId, statePeriod]);
}

export default useInitSubscribeOverlay;
